import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { env } from "../lib/env";
import { getAccessToken, renewSession } from "../lib/auth/oidc";
import type { ApiErrorPayload } from "./types";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly fields?: string[] | undefined,
    public readonly correlationId?: string | undefined,
    message?: string,
  ) {
    super(message ?? code);
    this.name = "ApiError";
  }
}

/** Message d'erreur affichable : message serveur si ApiError, sinon repli réseau. */
export function toErrorMessage(e: unknown): string {
  return e instanceof ApiError ? e.message : "Erreur réseau";
}

type RetriedConfig = InternalAxiosRequestConfig & {
  _retriedAfterRenew?: boolean;
};

/** Draine toutes les pages d'un endpoint keyset-paginé ({items, next_cursor}).
 * Sans ce suivi, le défaut backend (50) tronque silencieusement listes et
 * compteurs. */
// ponytail: cap à 10 pages (10×200 items) — passer à une vraie pagination
// infinie (useInfiniteQuery) si un tenant dépasse ce volume.
export async function fetchAllPages<T>(
  fetchPage: (cursor?: string) => Promise<{ items: T[]; next_cursor?: string }>,
  maxPages = 10,
): Promise<{ items: T[] }> {
  const items: T[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < maxPages; page++) {
    const res = await fetchPage(cursor);
    items.push(...res.items);
    cursor = res.next_cursor;
    if (!cursor) break;
  }
  return { items };
}

export function createModuleClient(basePath: string): AxiosInstance {
  const client = axios.create({
    baseURL: `${env.VITE_API_CENTRAL_BASE_URL.replace(/\/$/, "")}${basePath}`,
    timeout: 15000,
    headers: { Accept: "application/json", "Content-Type": "application/json" },
  });

  client.interceptors.request.use(async (config) => {
    const token = await getAccessToken();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    const cid =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `cid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    config.headers.set("X-Correlation-ID", cid);
    return config;
  });

  client.interceptors.response.use(
    (r) => r,
    async (error: AxiosError<ApiErrorPayload>) => {
      const cfg = error.config as RetriedConfig | undefined;
      const status = error.response?.status ?? 0;
      const correlationId = (cfg?.headers?.get?.("X-Correlation-ID") ??
        undefined) as string | undefined;

      if (status === 401 && cfg && !cfg._retriedAfterRenew) {
        cfg._retriedAfterRenew = true;
        try {
          await renewSession();
          // rejouer seulement si le renouvellement a réussi ; sinon le retry
          // renverrait le même token expiré → 401 + aller-retour réseau inutile.
          return client.request(cfg);
        } catch {
          // renouvellement échoué → on tombe sur la construction d'ApiError
        }
      }

      const payload = error.response?.data;
      const code =
        payload && typeof payload === "object" && "error" in payload
          ? String(payload.error)
          : (error.code ?? "network_error");
      const message =
        payload && typeof payload === "object" && "message" in payload
          ? String(
              (payload as ApiErrorPayload & { message?: string }).message ?? "",
            )
          : error.message;
      const fields =
        payload &&
        typeof payload === "object" &&
        Array.isArray((payload as ApiErrorPayload).fields)
          ? (payload as ApiErrorPayload).fields
          : undefined;

      throw new ApiError(
        status,
        code,
        fields,
        correlationId,
        message || undefined,
      );
    },
  );

  return client;
}

function makeRequest(client: AxiosInstance) {
  return async function request<T>(config: AxiosRequestConfig): Promise<T> {
    const res: AxiosResponse<T> = await client.request<T>(config);
    return res.data;
  };
}

// TM = cœur d'écriture ; TAWA = annuaire membres/équipes ; PM et CRM = lecture
// seule (contexte projet, Party/contacts) — cf. DT Senacle Todo §4 bis.
export const tasksClient = createModuleClient("/api/v1/tasks");
export const tawaClient = createModuleClient("/api/v1/tawa");
export const pmClient = createModuleClient("/api/v1/pm");
export const crmClient = createModuleClient("/api/v1/crm");

export const tasksRequest = makeRequest(tasksClient);
export const tawaRequest = makeRequest(tawaClient);
export const pmRequest = makeRequest(pmClient);
export const crmRequest = makeRequest(crmClient);

export default tasksClient;
